import React from 'react';
import styled from 'styled-components';
import DatePicker from 'react-datepicker';
import { differenceInDays, isWeekend } from 'date-fns';
import { ReactDatePickerStyle } from 'styles/ReactDatePickerStyle';
import { Text } from 'components/Text';
import { RadioList, RadioListItem } from 'components/RadioList';
import { futureDate, formatDate } from 'utils/date';

type PickupType =
  | 'today'
  | 'tomorrow'
  | 'days2'
  | 'days3'
  | 'days4'
  | 'days5'
  | 'other';

type Props = {
  value?: Date;
  hasWeekends?: boolean;
  onChange?: (pickup: Date) => void;
};

const Pickup = (props: Props) => {
  const { value: pickup, onChange = () => {} } = props;
  const { current: dates } = React.useRef<{ [key in PickupType]: Date }>({
    today: new Date(),
    tomorrow: futureDate(1),
    days2: futureDate(2),
    days3: futureDate(3),
    days4: futureDate(4),
    days5: futureDate(5),
    other: futureDate(6),
  });

  const pickupType = Object.keys(dates).reduce(
    (acc, key) => {
      const type = key as keyof typeof dates;
      return pickup && differenceInDays(dates[type], pickup) === 0 ? type : acc;
    },
    pickup ? 'other' : undefined
  );

  const otherDate = pickupType === 'other' ? pickup : undefined;

  function handlePickupChange(
    event: React.ChangeEvent<React.ElementRef<typeof RadioListItem>>
  ) {
    const radio = event.currentTarget as HTMLInputElement;
    const type = radio.value as keyof typeof dates;
    onChange(dates[type]);
  }

  function handleOtherPickupChange(date: Date) {
    onChange(date);
  }

  const radioItemSharedProps = {
    fill: true,
    small: true,
    onChange: handlePickupChange,
  };

  return (
    <ReactDatePickerStyle as={RadioList} direction="row" spacing={[0, 'small']}>
      {hasToday() && (
        <RadioListItem
          value="today"
          checked={pickupType === 'today'}
          {...radioItemSharedProps}
        >
          <PickupText weight="medium">Today</PickupText>
          <PickupText font="tiny">{formatDate(dates.today)}</PickupText>
        </RadioListItem>
      )}
      <RadioListItem
        value="tomorrow"
        checked={pickupType === 'tomorrow'}
        {...radioItemSharedProps}
      >
        <PickupText weight="medium">Tomorrow</PickupText>
        <PickupText font="tiny">{formatDate(dates.tomorrow)}</PickupText>
      </RadioListItem>
      <RadioListItem
        value="days2"
        checked={pickupType === 'days2'}
        {...radioItemSharedProps}
      >
        <PickupText weight="medium">In 2 days</PickupText>
        <PickupText font="tiny">{formatDate(dates.days2)}</PickupText>
      </RadioListItem>
      <RadioListItem
        value="days3"
        checked={pickupType === 'days3'}
        {...radioItemSharedProps}
      >
        <PickupText weight="medium">In 3 days</PickupText>
        <PickupText font="tiny">{formatDate(dates.days3)}</PickupText>
      </RadioListItem>
      <RadioListItem
        value="days4"
        checked={pickupType === 'days4'}
        {...radioItemSharedProps}
      >
        <PickupText weight="medium">In 4 days</PickupText>
        <PickupText font="tiny">{formatDate(dates.days4)}</PickupText>
      </RadioListItem>
      <RadioListItem
        value="days5"
        checked={pickupType === 'days5'}
        {...radioItemSharedProps}
      >
        <PickupText weight="medium">In 5 days</PickupText>
        <PickupText font="tiny">{formatDate(dates.days5)}</PickupText>
      </RadioListItem>
      <DatePicker
        selected={otherDate}
        minDate={dates.other}
        popperPlacement="bottom-end"
        filterDate={(date: Date) => !isWeekend(date)}
        onChange={handleOtherPickupChange}
        customInput={
          <div>
            <OtherRadioListItem
              value="other"
              checked={pickupType === 'other'}
              onChange={() => handleOtherPickupChange(dates.other)}
              readOnly
              fill
              small
            >
              <PickupText weight="medium">Other</PickupText>
              <OtherPickupDate font="tiny">
                {otherDate ? formatDate(otherDate) : 'Custom date'}
              </OtherPickupDate>
            </OtherRadioListItem>
          </div>
        }
      />
    </ReactDatePickerStyle>
  );
};

function hasToday() {
  const timeZone = 'Europe/Madrid';
  const cetString = new Date().toLocaleString('en', { timeZone });
  const cetDate = new Date(cetString);

  return cetDate.getHours() <= 13;
}

const PickupText = styled(Text)`
  display: block;
  white-space: nowrap;

  & + & {
    margin-top: 4px;
  }
`;

const OtherRadioListItem = styled(RadioListItem)`
  display: flex;
`;

const OtherPickupDate = styled(PickupText)`
  text-transform: uppercase;
`;

export { Pickup };
